import React, { useState } from 'react';
import { PlusIcon, PhoneIcon, PencilIcon, TrashIcon, XMarkIcon } from '@heroicons/react/24/outline';
import {
  useListMyPickupContactsQuery,
  useAddMyPickupContactMutation,
  useUpdateMyPickupContactMutation,
  useDeleteMyPickupContactMutation,
  useGetMyChildrenQuery,
} from '../../store/api/localApi';
import type { PickupContactGroupShape } from '../../types/attendance-v2';
import { useToast } from '../../components/Toast';

const RELATIONSHIP_OPTIONS = [
  { value: 'GRANDPARENT', label: 'Grandparent' },
  { value: 'AUNT_UNCLE', label: 'Aunt / Uncle' },
  { value: 'SIBLING', label: 'Sibling (16+)' },
  { value: 'FAMILY_FRIEND', label: 'Family friend' },
  { value: 'NEIGHBOUR', label: 'Neighbour' },
  { value: 'OTHER', label: 'Other' },
];

function relationshipLabel(type: string): string {
  return RELATIONSHIP_OPTIONS.find((o) => o.value === type)?.label ?? '';
}

interface ContactFormState {
  name: string;
  relationship: string;
  relationshipType: string;
  phone: string;
  studentIds: string[];
}

const emptyForm = (allStudentIds: string[]): ContactFormState => ({
  name: '',
  relationship: '',
  relationshipType: 'GRANDPARENT',
  phone: '',
  studentIds: allStudentIds,
});

const ParentAccountPickupContacts: React.FC = () => {
  const { showError, showSuccess } = useToast();

  const { data: childrenData, isLoading: childrenLoading } = useGetMyChildrenQuery();
  const children = childrenData?.children ?? [];
  const allChildIds = children.map((c) => c.id);

  const { data, isFetching } = useListMyPickupContactsQuery();
  const groups = data?.groups ?? [];

  const [addContact, { isLoading: adding }] = useAddMyPickupContactMutation();
  const [updateContact, { isLoading: updating }] = useUpdateMyPickupContactMutation();
  const [deleteContact] = useDeleteMyPickupContactMutation();

  const [showAddForm, setShowAddForm] = useState(false);
  const [editingGroup, setEditingGroup] = useState<PickupContactGroupShape | null>(null);
  const [form, setForm] = useState<ContactFormState>(emptyForm(allChildIds));

  const openAdd = () => {
    setForm(emptyForm(allChildIds));
    setShowAddForm(true);
    setEditingGroup(null);
  };

  const openEdit = (group: PickupContactGroupShape) => {
    setForm({
      name: group.name,
      relationship: group.relationship,
      relationshipType: group.relationshipType,
      phone: group.phone ?? '',
      studentIds: group.students.map((s) => s.id),
    });
    setEditingGroup(group);
    setShowAddForm(false);
  };

  const closeForm = () => {
    setShowAddForm(false);
    setEditingGroup(null);
  };

  const toggleStudent = (studentId: string) => {
    setForm((prev) => ({
      ...prev,
      studentIds: prev.studentIds.includes(studentId)
        ? prev.studentIds.filter((id) => id !== studentId)
        : [...prev.studentIds, studentId],
    }));
  };

  const handleAdd = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.name.trim() || !form.relationship.trim()) {
      showError('Please fill in a name and a description.');
      return;
    }
    if (form.studentIds.length === 0) {
      showError('Select at least one child.');
      return;
    }
    try {
      await addContact({
        name: form.name.trim(),
        relationship: form.relationship.trim(),
        relationshipType: form.relationshipType,
        phone: form.phone.trim() || undefined,
        studentIds: form.studentIds,
      }).unwrap();
      showSuccess(`${form.name} added.`);
      closeForm();
    } catch (err: any) {
      showError(err?.data?.message || err?.data?.error || 'Could not add contact.');
    }
  };

  const handleUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingGroup) return;
    const isGuardianEdit = editingGroup.isGuardian;
    if (!form.name.trim() || (!isGuardianEdit && !form.relationship.trim())) {
      showError('Please fill in a name and a description.');
      return;
    }
    if (!isGuardianEdit && form.studentIds.length === 0) {
      showError('Select at least one child.');
      return;
    }
    try {
      await updateContact({
        groupId: editingGroup.groupId,
        name: form.name.trim(),
        relationship: form.relationship.trim(),
        relationshipType: form.relationshipType,
        phone: form.phone.trim() || undefined,
        studentIds: form.studentIds,
        isGuardian: editingGroup.isGuardian || false,
      }).unwrap();
      showSuccess(`${form.name} updated.`);
      closeForm();
    } catch (err: any) {
      showError(err?.data?.message || err?.data?.error || 'Could not update contact.');
    }
  };

  const handleDelete = async (group: PickupContactGroupShape) => {
    if (!window.confirm(`Remove ${group.name} from all your children's pickup lists?`)) return;
    try {
      await deleteContact({ groupId: group.groupId }).unwrap();
      showSuccess(`${group.name} removed.`);
    } catch (err: any) {
      showError(err?.data?.message || err?.data?.error || 'Could not remove contact.');
    }
  };

  if (childrenLoading) {
    return (
      <div className="min-h-[40vh] flex items-center justify-center text-sm text-gray-500">
        Loading…
      </div>
    );
  }

  const guardianGroups = groups.filter((g) => g.isGuardian);
  const extraGroups = groups.filter((g) => !g.isGuardian);

  return (
    <div className="py-6" data-testid="parent-pickup-contacts-page">
      <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 space-y-5">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Authorised pickup</h1>
          <p className="mt-1 text-sm text-gray-500">
            Anyone on this list can collect your children from after-school. Staff verify by name at
            pickup.
          </p>
        </div>

        {isFetching && groups.length === 0 && (
          <p className="text-sm text-gray-500 italic">Loading contacts…</p>
        )}

        {/* Guardians (parents on file) — not removable */}
        {guardianGroups.length > 0 && (
          <section className="bg-white rounded-lg shadow">
            <div className="px-4 py-2.5 border-b border-gray-100">
              <h2 className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                Parents on file
              </h2>
            </div>
            <ul className="divide-y divide-gray-100">
              {guardianGroups.map((g) =>
                editingGroup?.groupId === g.groupId && editingGroup?.isGuardian ? (
                  <li key={g.groupId} className="px-4 py-4">
                    <ContactForm
                      form={form}
                      setForm={setForm}
                      children={children}
                      allChildIds={allChildIds}
                      onSubmit={handleUpdate}
                      onClose={closeForm}
                      isEditing
                      isGuardian
                      isSaving={updating}
                      toggleStudent={toggleStudent}
                      inline
                    />
                  </li>
                ) : (
                  <li key={g.groupId} className="px-4 py-3 flex items-center gap-3">
                    <Avatar name={g.name} tone="emerald" />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-gray-900 truncate">{g.name}</div>
                      <div className="text-xs text-gray-500 truncate">{g.relationship}</div>
                      {g.phone && (
                        // min-w-0 + truncate, matching the name and relationship
                        // rows above. Without them this flex row cannot shrink
                        // and has no ellipsis, so on a 390px viewport the number
                        // rendered to right=420 — 30px past the viewport, clipped
                        // by the overflow-hidden parent with no visual cue. This
                        // is the screen staff read to verify who may collect a
                        // child, so a silently half-shown phone number matters.
                        <div className="text-xs text-gray-400 mt-0.5 flex items-center gap-1 min-w-0">
                          <PhoneIcon className="h-3 w-3 flex-shrink-0" />
                          <span className="truncate">{g.phone}</span>
                        </div>
                      )}
                      <ChildChips students={g.students} />
                    </div>
                    <button
                      type="button"
                      data-testid={`pickup-contact-edit-${g.groupId}`}
                      onClick={() => openEdit(g)}
                      className="p-1.5 text-gray-400 hover:text-primary-600 rounded flex-shrink-0"
                      aria-label={`Edit ${g.name}`}
                    >
                      <PencilIcon className="h-4 w-4" />
                    </button>
                  </li>
                )
              )}
            </ul>
          </section>
        )}

        {/* Extra authorised contacts */}
        <section className="bg-white rounded-lg shadow">
          {extraGroups.length === 0 ? (
            <div className="px-4 py-3 text-xs text-gray-400 italic">
              No extra contacts yet. Add a grandparent, neighbour, or anyone else you authorise to
              collect.
            </div>
          ) : (
            <ul className="divide-y divide-gray-100">
              {extraGroups.map((g) =>
                editingGroup?.groupId === g.groupId && !editingGroup?.isGuardian ? (
                  /* Inline edit form — replaces the card so the user sees it immediately */
                  <li key={g.groupId} className="px-4 py-4">
                    <ContactForm
                      form={form}
                      setForm={setForm}
                      children={children}
                      allChildIds={allChildIds}
                      onSubmit={handleUpdate}
                      onClose={closeForm}
                      isEditing
                      isSaving={updating}
                      toggleStudent={toggleStudent}
                      inline
                    />
                  </li>
                ) : (
                  <li
                    key={g.groupId}
                    data-testid={`pickup-contact-group-${g.groupId}`}
                    className="px-4 py-3 flex items-start gap-3"
                  >
                    <Avatar name={g.name} tone="amber" />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-gray-900 truncate">{g.name}</div>
                      <div className="text-xs text-gray-500 truncate">
                        <span className="font-medium">{relationshipLabel(g.relationshipType)}</span>
                        {g.relationship && (
                          <span className="text-gray-400"> · {g.relationship}</span>
                        )}
                      </div>
                      {/*
                        The phone gets its OWN row rather than sitting inline
                        after the relationship. An `inline-flex` child inside a
                        `truncate` parent opts out of the ellipsis and overflows
                        instead: at 390px this rendered to right=420 — 30px past
                        the viewport — with the number silently cut off and no
                        visual cue. This is the list staff read to verify who
                        may collect a child, so a half-shown number matters.
                        min-w-0 lets the flex row shrink; truncate gives the
                        number an ellipsis when it still doesn't fit.
                      */}
                      {g.phone && (
                        <div className="text-xs text-gray-400 mt-0.5 flex items-center gap-1 min-w-0">
                          <PhoneIcon className="h-3 w-3 flex-shrink-0" />
                          <span className="truncate">{g.phone}</span>
                        </div>
                      )}
                      <ChildChips students={g.students} />
                    </div>
                    <div className="flex items-center gap-1 flex-shrink-0">
                      <button
                        type="button"
                        data-testid={`pickup-contact-edit-${g.groupId}`}
                        onClick={() => openEdit(g)}
                        className="p-1.5 text-gray-400 hover:text-primary-600 rounded"
                        aria-label={`Edit ${g.name}`}
                      >
                        <PencilIcon className="h-4 w-4" />
                      </button>
                      <button
                        type="button"
                        data-testid={`pickup-contact-delete-${g.groupId}`}
                        onClick={() => handleDelete(g)}
                        className="p-1.5 text-gray-400 hover:text-rose-600 rounded"
                        aria-label={`Remove ${g.name}`}
                      >
                        <TrashIcon className="h-4 w-4" />
                      </button>
                    </div>
                  </li>
                )
              )}
            </ul>
          )}
        </section>

        {/* Add form — only shown when not in inline-edit mode */}
        {!editingGroup &&
          (showAddForm ? (
            <ContactForm
              form={form}
              setForm={setForm}
              children={children}
              allChildIds={allChildIds}
              onSubmit={handleAdd}
              onClose={closeForm}
              isEditing={false}
              isSaving={adding}
              toggleStudent={toggleStudent}
            />
          ) : (
            <button
              type="button"
              onClick={openAdd}
              data-testid="pickup-contact-add"
              className="w-full p-3 rounded-lg border border-dashed border-gray-300 text-sm font-medium text-gray-600 hover:bg-white hover:border-emerald-400 hover:text-emerald-700 transition inline-flex items-center justify-center gap-2"
            >
              <PlusIcon className="h-4 w-4" />
              Add a contact
            </button>
          ))}

        <p className="text-xs text-gray-400 italic">
          Need a one-off pickup today? Call the location and they'll add the person while you're on
          the line.
        </p>
      </div>
    </div>
  );
};

interface ContactFormProps {
  form: ContactFormState;
  setForm: React.Dispatch<React.SetStateAction<ContactFormState>>;
  children: { id: string; firstName: string; lastName?: string; locationName?: string }[];
  allChildIds: string[];
  onSubmit: (e: React.FormEvent) => void;
  onClose: () => void;
  isEditing: boolean;
  /** When true, name is read-only and student picker is hidden (guardian entry). */
  isGuardian?: boolean;
  isSaving: boolean;
  toggleStudent: (id: string) => void;
  /** When true, renders without card chrome — used inside an existing list item. */
  inline?: boolean;
}

const ContactForm: React.FC<ContactFormProps> = ({
  form,
  setForm,
  children,
  allChildIds,
  onSubmit,
  onClose,
  isEditing,
  isGuardian = false,
  isSaving,
  toggleStudent,
  inline = false,
}) => (
  <form
    onSubmit={onSubmit}
    className={inline ? 'space-y-3' : 'bg-white rounded-lg shadow p-4 space-y-3'}
    data-testid="pickup-contact-form"
  >
    <div className="flex items-baseline justify-between">
      <h2 className="text-base font-semibold text-gray-900">
        {isEditing ? 'Edit contact' : 'Add a contact'}
      </h2>
      <button
        type="button"
        onClick={onClose}
        aria-label="Close"
        className="text-gray-400 hover:text-gray-600"
      >
        <XMarkIcon className="h-5 w-5" />
      </button>
    </div>

    <div>
      <label className="block text-sm font-medium text-gray-700">Full name</label>
      {isGuardian ? (
        <div className="mt-1">
          <p className="text-sm text-gray-900">{form.name}</p>
          <p className="text-xs text-gray-400 mt-0.5">Name comes from your account profile.</p>
        </div>
      ) : (
        <input
          type="text"
          value={form.name}
          onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
          required
          data-testid="pickup-contact-form-name"
          placeholder="e.g. Mary Ryan"
          className="mt-1 w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
        />
      )}
    </div>

    {!isGuardian && (
      <>
        <div>
          <label className="block text-sm font-medium text-gray-700">
            Relationship <span className="text-rose-500">*</span>
          </label>
          <select
            value={form.relationshipType}
            onChange={(e) => setForm((p) => ({ ...p, relationshipType: e.target.value }))}
            data-testid="pickup-contact-form-relationship-type"
            className="mt-1 w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
          >
            {RELATIONSHIP_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700">
            Short description <span className="text-rose-500">*</span>
          </label>
          <input
            type="text"
            value={form.relationship}
            onChange={(e) => setForm((p) => ({ ...p, relationship: e.target.value }))}
            required
            data-testid="pickup-contact-form-relationship"
            placeholder="e.g. Grandmother on dad's side"
            className="mt-1 w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
          />
        </div>
      </>
    )}

    <div>
      <label className="block text-sm font-medium text-gray-700">
        Phone <span className="text-gray-400 font-normal">(optional)</span>
      </label>
      <input
        type="tel"
        value={form.phone}
        onChange={(e) => setForm((p) => ({ ...p, phone: e.target.value }))}
        data-testid="pickup-contact-form-phone"
        placeholder="087 123 4567"
        className="mt-1 w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
      />
    </div>

    {/* Child picker — hidden for guardians (always linked to all children) and single-child parents */}
    {!isGuardian && allChildIds.length > 1 && (
      <div>
        <label className="block text-sm font-medium text-gray-700">
          Authorised to pick up <span className="text-rose-500">*</span>
        </label>
        <div className="mt-1 space-y-1.5">
          {children.map((child) => (
            <label
              key={child.id}
              className="flex items-center gap-2.5 text-sm text-gray-700 cursor-pointer"
            >
              <input
                type="checkbox"
                data-testid={`pickup-contact-kid-${child.id}`}
                checked={form.studentIds.includes(child.id)}
                onChange={() => toggleStudent(child.id)}
                className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
              />
              {child.firstName} {child.lastName ?? ''}
            </label>
          ))}
        </div>
      </div>
    )}

    {isGuardian ? (
      <div className="bg-gray-50 border border-gray-200 rounded p-3 text-xs text-gray-600">
        Parents on file are always authorised to collect. Only the phone number can be updated here.
      </div>
    ) : (
      <div className="bg-blue-50 border border-blue-200 rounded p-3 text-xs text-blue-900">
        By adding this person you confirm they're authorised to collect your{' '}
        {allChildIds.length === 1 ? 'child' : 'children'}. Staff will check their name at pickup.
      </div>
    )}

    <button
      type="submit"
      disabled={isSaving}
      data-testid={isEditing ? 'pickup-contact-update' : 'pickup-contact-save'}
      className="w-full py-2 px-4 rounded-md bg-emerald-600 text-white text-sm font-semibold hover:bg-emerald-700 disabled:opacity-50"
    >
      {isSaving ? 'Saving…' : isEditing ? 'Save changes' : `Add ${form.name || 'contact'}`}
    </button>
  </form>
);

const ChildChips: React.FC<{
  students: { id: string; firstName?: string; lastName?: string }[];
}> = ({ students }) => {
  if (students.length === 0) return null;
  return (
    <div className="mt-1 flex flex-wrap gap-1">
      {students.map((s) => (
        <span
          key={s.id}
          className="inline-flex items-center px-1.5 py-0.5 rounded text-xs bg-primary-50 text-primary-700"
        >
          {s.firstName ?? s.id}
        </span>
      ))}
    </div>
  );
};

const Avatar: React.FC<{ name: string; tone: 'emerald' | 'amber' }> = ({ name, tone }) => {
  const initials =
    name
      .split(' ')
      .map((p) => p[0])
      .filter(Boolean)
      .slice(0, 2)
      .join('')
      .toUpperCase() || '?';
  const cls =
    tone === 'emerald' ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700';
  return (
    <div
      aria-hidden="true"
      className={`h-9 w-9 rounded-full flex items-center justify-center text-sm font-semibold flex-shrink-0 ${cls}`}
    >
      {initials}
    </div>
  );
};

export default ParentAccountPickupContacts;
